import { tours as catalogTours, type Tour } from '@/mocks/tours';
import { tourDetails } from '@/mocks/tourDetails';
import { getSiteSettings, setSiteSettings } from './siteSettings';

/** How the online checkout charges a tour: a deposit now and the rest on board, or everything now. */
export type ChargeMode = 'deposit' | 'full';

export interface TourAdminOverride {
  /** 'pix' takes the online deposit; 'whatsapp' only asks for a quote. Unset = the tour's default (lib/onlinePayment). */
  bookingMode?: 'pix' | 'whatsapp';
  /** 'total' means the price pays for the whole private boat or group, whatever the number of people. */
  priceMode?: 'person' | 'total';
  /** Adult price in BRL (the whole tour's price when priceMode is 'total'). */
  price?: number;
  /** Fixed child price in BRL. Unset = half of the adult price. */
  childPrice?: number;
  /** Fixed baby price in BRL. Unset = free. */
  babyPrice?: number;
  chargeMode?: ChargeMode;
  /** Deposit percentage for this tour (0–100). Unset = the general percentage from Ajustes. */
  depositPercent?: number;
  /** 'fixed' charges `depositAmount` reais instead of a percentage. Unset = percentage. */
  depositType?: 'percent' | 'fixed';
  /** Fixed deposit in BRL… */
  depositAmount?: number;
  /** …per paying person (adults, children, babies with a price) or once per booking of the tour. */
  depositPer?: 'person' | 'booking';
  title?: string;
  subtitle?: string;
  description?: string;
  longDescription?: string;
  imageUrl?: string;
  /** Photos of the tour page gallery, in order. Unset = the original gallery. */
  gallery?: string[];
  availability?: number;
  /** What the owner keeps of each sale (admin Comisiones): a share of the sale or a fixed amount… */
  commissionType?: 'percent' | 'fixed';
  /** …this many percent, or this many reais. Unset = the default (escunas 30%), or "not set" elsewhere. */
  commissionValue?: number;
  /** A fixed amount counts once per person or once per booking. */
  commissionPer?: 'person' | 'booking';
  /** false turns off the tour's built-in flash offer (the short timer each visitor sees). */
  flashEnabled?: boolean;
  flashMinutes?: number;
  /** Flash offer set in the admin (Ofertas): adult price while it runs… */
  flashPrice?: number;
  /** …until this moment (ISO), the same for every visitor. */
  flashEndsAt?: string;
}

/** The admin's flash offer of a tour while it is running, or null. */
export function activeFlashOffer(override: TourAdminOverride | undefined, now = Date.now()) {
  if (!override?.flashPrice || !override.flashEndsAt) return null;
  const endsAt = Date.parse(override.flashEndsAt);
  return Number.isFinite(endsAt) && endsAt > now ? { price: override.flashPrice, endsAt: override.flashEndsAt } : null;
}

/** Tour edits used to live only in the editing browser; the admin moves them to the server once. */
const LEGACY_STORAGE_KEY = 'aquamarina_admin_tour_overrides_v1';

/** Tour edits made in the admin, as loaded from the server (see lib/siteSettings). */
export function getTourOverrides(): Record<string, TourAdminOverride> {
  return getSiteSettings().tourOverrides || {};
}

export function getLegacyTourOverrides(): Record<string, TourAdminOverride> {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, TourAdminOverride>) : {};
  } catch {
    return {};
  }
}

export function clearLegacyTourOverrides() {
  try { localStorage.removeItem(LEGACY_STORAGE_KEY); } catch { /* storage blocked */ }
}

/** Slugs differ in casing between the local catalog and Supabase, so lookups ignore case. */
export function getTourOverride(slug: string | undefined | null): TourAdminOverride | undefined {
  if (!slug) return undefined;
  const overrides = getTourOverrides();
  if (overrides[slug]) return overrides[slug];
  const target = slug.toLowerCase();
  const key = Object.keys(overrides).find((item) => item.toLowerCase() === target);
  return key ? overrides[key] : undefined;
}

/**
 * Shows a tour edit in this browser right away. The admin then saves it on the server
 * (pages/admin/tourSettings.ts), which is what every visitor sees.
 */
export function saveTourOverride(slug: string, override: TourAdminOverride) {
  const overrides = { ...getTourOverrides() };
  const merged: TourAdminOverride = { ...overrides[slug], ...override };
  // An undefined value means "back to the original", so it is removed instead of stored.
  (Object.keys(merged) as Array<keyof TourAdminOverride>).forEach((key) => {
    if (merged[key] === undefined) delete merged[key];
  });
  if (Object.keys(merged).length) overrides[slug] = merged;
  else delete overrides[slug];
  setSiteSettings({ ...getSiteSettings(), tourOverrides: overrides });
}

/**
 * Applies the operator's edits on top of a tour. It must run last — after Supabase data and translations —
 * so what the operator typed in the admin is what visitors see, in every language.
 */
export function applyTourOverride<T extends Tour>(tour: T): T {
  const override = getTourOverride(tour.slug);
  if (!override) return tour;
  const text = (value: string | undefined, fallback: string) => (value && value.trim() ? value : fallback);
  const gallery = override.gallery?.filter(Boolean);
  const builtInDeal = override.flashEnabled === false
    ? undefined
    : tour.flashDeal && override.price != null
      ? scaleFlashDeal(tour, override.price)
      : tour.flashDeal;
  // An offer set in the admin replaces the built-in one while it runs; before and after it the tour costs its
  // regular price (the price typed in the admin, else what it costs outside the built-in offer).
  const offer = activeFlashOffer(override);
  const regular = override.price ?? (builtInDeal?.postOfferPrice ?? tour.price);
  const result = {
    ...tour,
    title: text(override.title, tour.title),
    subtitle: text(override.subtitle, tour.subtitle),
    description: text(override.description, tour.description),
    price: offer ? regular : override.price ?? tour.price,
    imageUrl: override.imageUrl || tour.imageUrl,
    gallery: gallery && gallery.length > 0 ? gallery : tour.gallery,
    imagesCount: gallery && gallery.length > 0 ? gallery.length : tour.imagesCount,
    flashDeal: offer && offer.price < regular
      ? { originalPrice: regular, dealPrice: offer.price, endsAt: offer.endsAt }
      : builtInDeal,
  } as T;
  if ('longDescription' in tour) {
    (result as T & { longDescription: string }).longDescription = text(override.longDescription, (tour as T & { longDescription: string }).longDescription);
  }
  return result;
}

/**
 * A new adult price moves the flash deal with it: the offer keeps the same discount it had, and once it ends
 * the tour costs exactly the price typed in the admin (not the old post-offer price).
 */
export function scaleFlashDeal(tour: Pick<Tour, 'price' | 'flashDeal'>, price: number): Tour['flashDeal'] {
  const deal = tour.flashDeal;
  if (!deal) return undefined;
  const regular = deal.postOfferPrice ?? tour.price;
  const factor = regular > 0 ? price / regular : 1;
  return {
    originalPrice: Math.round(deal.originalPrice * factor),
    dealPrice: Math.round(deal.dealPrice * factor),
    postOfferPrice: deal.postOfferPrice != null ? price : undefined,
  };
}

/** Child and baby price for a tour at a given adult price. */
export function getPassengerPrices(slug: string, adultPrice: number) {
  const override = getTourOverride(slug);
  return {
    childPrice: override?.childPrice ?? adultPrice * 0.5,
    babyPrice: override?.babyPrice ?? 0,
  };
}

export function getAvailability(slug: string): number | null {
  const value = getTourOverride(slug)?.availability;
  return typeof value === 'number' ? value : null;
}

/**
 * Photos uploaded in the admin are stored inline (data: URLs, ~150 KB each). They must never be copied into
 * carts or reservations, which would fill the browser storage; those keep '' and resolve the photo when shown.
 */
export const portableImage = (url: string) => (url.startsWith('data:') ? '' : url);

/** Photo for a cart item or reservation line: the stored one, the tour's admin photo, or its catalog photo. */
export function tourImageFor(slug: string, stored?: string): string | undefined {
  const target = slug.toLowerCase();
  const catalog = [...catalogTours, ...tourDetails].find((tour) => tour.slug.toLowerCase() === target)?.imageUrl;
  return stored || getTourOverride(slug)?.imageUrl || catalog || undefined;
}
